// The sessions list's device, OS and flag filters as chips (APS-400; Console D `#/sessions`'s
// "+ device, os, flag"). A chip is a key:value term of the same URL query the search box writes,
// so the two never disagree: set chips read `device: iPhone16,2 ×`, and their label reopens the
// value list to change it. The dashed chip opens a popover in two steps: the kinds not yet set,
// then that kind's values with their session counts (the top 20 by sessions, GET
// /sessions/facets and /flags), a filter field when there are more, and, for device and OS, the
// typed text as an exact value. Values are the app's: shown as text, never translated.
import { useQuery } from '@tanstack/react-query'
import {
  type CSSProperties,
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { flagsQuery, type SessionFilters, sessionFacetsQuery } from '../api/queries'
import { Icon } from '../components/icons'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { compactCount } from './Sessions'
import type { SessionsSearch } from './search'

export const FILTER_KINDS = ['device', 'os', 'flag'] as const
export type FilterKind = (typeof FILTER_KINDS)[number]

const KIND_NAME: Record<FilterKind, MessageId> = {
  device: 'sessions.filter.kind.device',
  os: 'sessions.filter.kind.os',
  flag: 'sessions.filter.kind.flag',
}
/** What a kind with no value at all in the range says (Mosaic): not a search without results. */
const KIND_NOTHING: Record<FilterKind, MessageId> = {
  device: 'sessions.filter.nothing.device',
  os: 'sessions.filter.nothing.os',
  flag: 'sessions.filter.nothing.flag',
}

/**
 * Where a popover goes (APS-483): 6 px under its anchor; when that runs past the window's foot
 * and it fits above, 6 px above it; otherwise as low as it fits, never above the window's top
 * (its list scrolls). Left: the anchor's, kept 8 px inside the window; on a phone, `row`'s left
 * and width.
 */
export function placePopover(
  anchor: { top: number; bottom: number; left: number },
  pop: { width: number; height: number },
  win: { width: number; height: number },
  row?: { left: number; width: number | undefined },
): { left: number; top: number; width?: number } {
  const gap = 6
  const margin = 8
  const below = anchor.bottom + gap
  const above = anchor.top - gap - pop.height
  const top =
    below + pop.height <= win.height - margin
      ? below
      : above >= margin
        ? above
        : Math.max(margin, Math.min(below, win.height - margin - pop.height))
  if (row) return { left: row.left, top, ...(row.width === undefined ? {} : { width: row.width }) }
  return { left: Math.max(margin, Math.min(anchor.left, win.width - pop.width - margin)), top }
}

/** One choice of step (b): what the URL gets, what it reads, and its sessions. */
export interface FilterValue {
  value: string
  label: string
  sessions: number
}

/** A flag value in the URL's shape: `name` (any variant) or `name=variant`. */
export const flagValue = (flag: string, variant?: string) =>
  variant === undefined ? flag : `${flag}=${variant}`

/** The URL keys a chip of this kind sets, from its value. */
export function patchOf(kind: FilterKind, value: string | undefined): Partial<SessionsSearch> {
  if (kind === 'device') return { device: value }
  if (kind === 'os') return { os: value }
  if (value === undefined) return { flag: undefined, variant: undefined }
  const at = value.indexOf('=')
  return at < 0
    ? { flag: value, variant: undefined }
    : { flag: value.slice(0, at), variant: value.slice(at + 1) }
}

/** A set chip's value as it reads: `new_checkout = on`, or the flag alone for any variant. */
export function chipText(kind: FilterKind, s: SessionsSearch): string | undefined {
  if (kind === 'device') return s.device
  if (kind === 'os') return s.os
  if (!s.flag) return undefined
  return s.variant === undefined ? s.flag : `${s.flag} = ${s.variant}`
}

/** The case-insensitive match of the filter field over a value's text. */
export const matches = (v: FilterValue, text: string) =>
  !text.trim() || v.label.toLowerCase().includes(text.trim().toLowerCase())

export function SessionFilterChips({
  projectId,
  search,
  filters,
  onChange,
  kinds = FILTER_KINDS,
  inert,
  addLabel,
  compact,
}: {
  projectId: string
  search: SessionsSearch
  /** The list's filters, for the counts beside the values. */
  filters: SessionFilters & { days: number }
  onChange: (patch: Partial<SessionsSearch>) => void
  /** The kinds this place offers (the bar: device and OS, and flag on Issues; APS-228). */
  kinds?: readonly FilterKind[]
  /**
   * Why the page does not apply them (APS-228): set chips show dimmed with it as their title,
   * still clearable, and nothing can be added.
   */
  inert?: string | undefined
  /** The add chip's words; the kinds left (`+ device, os, flag`) when absent. */
  addLabel?: string | undefined
  /**
   * The bar's form (Mosaic, APS-228): no set chips, one chip instead. `+ Filter` with none set;
   * `Filters · N` with N, whose popover lists them first (each reopening its values, each
   * removable), then the kinds left. Inert, it shows dimmed and lists them to remove only.
   */
  compact?: { label: string } | undefined
}) {
  const i18n = useI18n()
  const { t } = i18n
  const set = kinds.filter((k) => chipText(k, search) !== undefined)
  const free = inert ? [] : kinds.filter((k) => !set.includes(k))
  const add = useRef<HTMLButtonElement>(null)
  const chips = useRef<Partial<Record<FilterKind, HTMLButtonElement | null>>>({})
  const sum = useRef<HTMLButtonElement>(null)
  // Open on a step: null is (a) the kinds; a kind is (b) its values; 'set' is the compact chip's
  // list of the set filters.
  const [open, setOpen] = useState<{
    step: FilterKind | null | 'set'
    from: FilterKind | 'add' | 'sum'
  } | null>(null)
  const anchor = () =>
    open?.from === 'add'
      ? add.current
      : open?.from === 'sum'
        ? sum.current
        : (chips.current[open?.from as FilterKind] ?? null)
  const close = (focus = true) => {
    const from = open?.from
    setOpen(null)
    if (!focus) return
    // Focus goes back to the '+', or to a chip when every kind is set.
    requestAnimationFrame(() => {
      const back =
        sum.current ??
        add.current ??
        (from && from !== 'add' && from !== 'sum' ? chips.current[from] : null)
      back?.focus()
    })
  }
  const pick = (kind: FilterKind, value: string) => {
    onChange(patchOf(kind, value))
    close()
  }

  const popover = open ? (
    <FilterPopover
      projectId={projectId}
      filters={filters}
      search={search}
      kinds={open.from === 'add' || open.from === 'sum' ? free : [open.from as FilterKind]}
      setKinds={compact ? set : []}
      inert={inert}
      step={open.step}
      anchor={anchor}
      onStep={(step) => setOpen({ ...open, step })}
      onPick={pick}
      onRemove={(kind) => {
        onChange(patchOf(kind, undefined))
        if (set.length <= 1) close()
      }}
      onClose={close}
      i18n={i18n}
    />
  ) : null

  if (compact) {
    if (set.length === 0 && (inert || free.length === 0)) return null
    const summary = set.map((k) => `${k}: ${chipText(k, search) ?? ''}`).join(', ')
    return (
      <span className="us-flt">
        {set.length === 0 ? (
          <button
            ref={add}
            type="button"
            className="chip add"
            aria-haspopup="dialog"
            aria-expanded={open?.from === 'add'}
            onClick={() => (open ? close(false) : setOpen({ step: null, from: 'add' }))}
          >
            <span aria-hidden="true">+ </span>
            <span>{addLabel ?? compact.label}</span>
          </button>
        ) : (
          <button
            ref={sum}
            type="button"
            className={inert ? 'chip us-sum off' : 'chip us-sum'}
            {...(inert ? { title: inert } : {})}
            aria-haspopup="dialog"
            aria-expanded={open?.from === 'sum'}
            // Its name starts with what it shows (WCAG 2.5.3).
            aria-label={`${compact.label}, ${set.length}: ${summary}${inert ? `. ${inert}` : ''}`}
            onClick={() => (open ? close(false) : setOpen({ step: 'set', from: 'sum' }))}
          >
            <span>{compact.label}</span>
            <span aria-hidden="true">·</span>
            <span>{set.length}</span>
          </button>
        )}
        {popover}
      </span>
    )
  }

  return (
    <span className="us-flt">
      {set.map((kind) => (
        <span
          key={kind}
          className={inert ? 'chip us-set off' : 'chip us-set'}
          {...(inert ? { title: inert } : {})}
        >
          <button
            type="button"
            className="us-cl"
            ref={(el) => {
              chips.current[kind] = el
            }}
            {...(inert
              ? {
                  'aria-disabled': true,
                  'aria-label': `${kind}: ${chipText(kind, search) ?? ''}. ${inert}`,
                }
              : {
                  'aria-haspopup': 'dialog' as const,
                  'aria-expanded': open?.from === kind,
                  // Its name starts with what it shows, the key as typed (WCAG 2.5.3).
                  'aria-label': t('sessions.filter.change', {
                    key: kind,
                    kind: t(KIND_NAME[kind]),
                    value: chipText(kind, search) ?? '',
                  }),
                })}
            onClick={() => {
              if (!inert) setOpen({ step: kind, from: kind })
            }}
          >
            <span>{kind}:</span>
            <span translate="no">{chipText(kind, search)}</span>
          </button>
          <button
            type="button"
            aria-label={t('sessions.filter.remove', { kind: t(KIND_NAME[kind]) })}
            onClick={() => {
              onChange(patchOf(kind, undefined))
              requestAnimationFrame(() => add.current?.focus())
            }}
          >
            <Icon name="x" small />
          </button>
        </span>
      ))}
      {free.length ? (
        <button
          ref={add}
          type="button"
          className="chip add"
          aria-haspopup="dialog"
          aria-expanded={open?.from === 'add'}
          onClick={() => (open ? close(false) : setOpen({ step: null, from: 'add' }))}
        >
          <span aria-hidden="true">+ </span>
          {addLabel ? (
            <span>{addLabel}</span>
          ) : (
            <>
              <span translate="no">{free.join(', ')}</span>
              <span className="vh">{t('sessions.filter.add')}</span>
            </>
          )}
        </button>
      ) : null}
      {popover}
    </span>
  )
}

function FilterPopover({
  projectId,
  filters,
  search,
  kinds,
  setKinds,
  inert,
  step,
  anchor,
  onStep,
  onPick,
  onRemove,
  onClose,
  i18n,
}: {
  projectId: string
  filters: SessionFilters & { days: number }
  search: SessionsSearch
  kinds: readonly FilterKind[]
  /** The compact chip's set filters, listed first ('set'); empty otherwise. */
  setKinds: readonly FilterKind[]
  inert: string | undefined
  step: FilterKind | null | 'set'
  anchor: () => HTMLElement | null
  onStep: (step: FilterKind | null | 'set') => void
  onPick: (kind: FilterKind, value: string) => void
  onRemove: (kind: FilterKind) => void
  onClose: (focus?: boolean) => void
  i18n: Translator
}) {
  const { t } = i18n
  const pop = useRef<HTMLDivElement>(null)
  const id = useId()
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' })

  // Under the anchor, or above it when it does not fit below (placePopover); on a phone, the
  // content's width (styles/sessions.css). Placed again when the step changes, when the popover
  // itself changes size (its values arrive after it opened: APS-483), on a resize and on a scroll;
  // a style that did not move is kept, so placing never loops.
  // biome-ignore lint/correctness/useExhaustiveDependencies: placed per step, not per render
  useLayoutEffect(() => {
    const place = () => {
      const a = anchor()?.getBoundingClientRect()
      const p = pop.current
      if (!a || !p) return
      const phone = window.innerWidth <= 640
      // The row it sits in: Sessions' filters, or the page head's on a narrow window (APS-228).
      const row = phone
        ? anchor()?.closest('.us-fl, .pg-scope')?.getBoundingClientRect()
        : undefined
      const next = placePopover(
        a,
        { width: p.offsetWidth, height: p.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        phone ? { left: row?.left ?? 16, width: row?.width } : undefined,
      )
      setStyle((prev) =>
        prev.left === next.left && prev.top === next.top && prev.width === next.width ? prev : next,
      )
    }
    place()
    const grows = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(place)
    if (pop.current) grows?.observe(pop.current)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      grows?.disconnect()
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [step])

  // A click outside closes it, focus staying where it went.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener while open
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (pop.current?.contains(target) || anchor()?.contains(target)) return
      onClose(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [])

  // On the body: the bar's backdrop filter would make it the fixed popover's containing block.
  return createPortal(
    <div
      ref={pop}
      className="pop us-pop"
      role="dialog"
      aria-label={
        step === 'set'
          ? t('shell.chip.filters')
          : step
            ? t(KIND_NAME[step])
            : t('sessions.filter.add')
      }
      style={style}
    >
      {step === 'set' ? (
        <SetMenu
          set={setKinds}
          kinds={inert ? [] : kinds}
          search={search}
          inert={inert}
          onStep={onStep}
          onRemove={onRemove}
          onClose={onClose}
          t={t}
        />
      ) : step === null ? (
        <KindMenu kinds={kinds} onStep={onStep} onClose={onClose} t={t} />
      ) : (
        <ValueList
          key={step}
          id={id}
          projectId={projectId}
          filters={filters}
          search={search}
          kind={step}
          back={setKinds.length > 0 || kinds.length > 1 || kinds[0] !== step}
          onBack={() => onStep(setKinds.length > 0 ? 'set' : null)}
          onPick={(v) => onPick(step, v)}
          onClose={onClose}
          i18n={i18n}
        />
      )}
    </div>,
    document.body,
  )
}

/**
 * The compact chip's first step (APS-228): the set filters, each a row whose label reopens its
 * values and whose × removes it, then the kinds not yet set. Arrows move, Esc closes. Inert (a
 * page that does not apply them), the rows only remove.
 */
function SetMenu({
  set,
  kinds,
  search,
  inert,
  onStep,
  onRemove,
  onClose,
  t,
}: {
  set: readonly FilterKind[]
  kinds: readonly FilterKind[]
  search: SessionsSearch
  inert: string | undefined
  onStep: (step: FilterKind) => void
  onRemove: (kind: FilterKind) => void
  onClose: (focus?: boolean) => void
  t: Translator['t']
}) {
  const items = useRef<(HTMLButtonElement | null)[]>([])
  useEffect(() => items.current.find(Boolean)?.focus(), [])
  const onKey = (e: KeyboardEvent) => {
    const list = items.current.filter((x): x is HTMLButtonElement => !!x)
    const at = list.indexOf(document.activeElement as HTMLButtonElement)
    const go = (j: number) => {
      e.preventDefault()
      list[(j + list.length) % list.length]?.focus()
    }
    if (e.key === 'ArrowDown') go(at + 1)
    else if (e.key === 'ArrowUp') go(at - 1)
    else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    } else if (e.key === 'Tab') onClose(false)
  }
  let n = 0
  const ref = () => {
    const i = n++
    return (el: HTMLButtonElement | null) => {
      items.current[i] = el
    }
  }
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the arrows move between its buttons
    <div className="us-setlist" onKeyDown={onKey}>
      {inert ? <p className="note us-inert">{inert}</p> : null}
      <ul aria-label={t('shell.chip.filters')}>
        {set.map((k) => (
          <li key={k}>
            {inert ? (
              <span className="us-cl" translate="no">
                <span>{k}:</span> {chipText(k, search)}
              </span>
            ) : (
              <button
                type="button"
                className="us-cl"
                ref={ref()}
                aria-label={t('sessions.filter.change', {
                  key: k,
                  kind: t(KIND_NAME[k]),
                  value: chipText(k, search) ?? '',
                })}
                onClick={() => onStep(k)}
              >
                <span>{k}:</span>
                <span translate="no">{chipText(k, search)}</span>
              </button>
            )}
            <button
              type="button"
              ref={ref()}
              aria-label={t('sessions.filter.remove', { kind: t(KIND_NAME[k]) })}
              onClick={() => onRemove(k)}
            >
              <Icon name="x" small />
            </button>
          </li>
        ))}
      </ul>
      {kinds.length ? (
        <div role="menu" aria-label={t('sessions.filter.add')} className="us-kinds">
          {kinds.map((k) => (
            <button key={k} type="button" role="menuitem" ref={ref()} onClick={() => onStep(k)}>
              {t(KIND_NAME[k])}
              <Icon name="right" small />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Step (a): the kinds not yet set, as a menu: arrows move, Enter opens one, Esc closes. */
function KindMenu({
  kinds,
  onStep,
  onClose,
  t,
}: {
  kinds: readonly FilterKind[]
  onStep: (step: FilterKind) => void
  onClose: (focus?: boolean) => void
  t: Translator['t']
}) {
  const items = useRef<(HTMLButtonElement | null)[]>([])
  useEffect(() => items.current[0]?.focus(), [])
  const onKey = (e: KeyboardEvent, i: number) => {
    const n = kinds.length
    const go = (j: number) => {
      e.preventDefault()
      items.current[(j + n) % n]?.focus()
    }
    if (e.key === 'ArrowDown') go(i + 1)
    else if (e.key === 'ArrowUp') go(i - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(n - 1)
    else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    } else if (e.key === 'Tab') onClose(false)
  }
  return (
    <div role="menu" aria-label={t('sessions.filter.add')} className="us-kinds">
      {kinds.map((k, i) => (
        <button
          key={k}
          type="button"
          role="menuitem"
          tabIndex={i === 0 ? 0 : -1}
          ref={(el) => {
            items.current[i] = el
          }}
          onClick={() => onStep(k)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t(KIND_NAME[k])}
          <Icon name="right" small />
        </button>
      ))}
    </div>
  )
}

/**
 * Step (b): a kind's values with their counts. The filter field (when there are more than the
 * list shows) is a combobox over the listbox; without it, the listbox takes the keys itself.
 */
function ValueList({
  id,
  projectId,
  filters,
  search,
  kind,
  back,
  onBack,
  onPick,
  onClose,
  i18n,
}: {
  id: string
  projectId: string
  filters: SessionFilters & { days: number }
  search: SessionsSearch
  kind: FilterKind
  back: boolean
  onBack: () => void
  onPick: (value: string) => void
  onClose: (focus?: boolean) => void
  i18n: Translator
}) {
  const { t } = i18n
  const facets = useQuery({ ...sessionFacetsQuery(projectId, filters), enabled: kind !== 'flag' })
  const flags = useQuery({
    ...flagsQuery(projectId, search.app, filters.days),
    enabled: kind === 'flag',
  })
  const [text, setText] = useState('')
  const [active, setActive] = useState(0)
  const field = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)

  const all: FilterValue[] =
    kind === 'device'
      ? (facets.data?.devices.values ?? []).map((v) => ({
          value: v.device_model,
          label: v.device_model,
          sessions: v.sessions,
        }))
      : kind === 'os'
        ? (facets.data?.os_versions.values ?? []).map((v) => ({
            value: v.os_version,
            label: `${v.os_name} ${v.os_version}`.trim(),
            sessions: v.sessions,
          }))
        : (flags.data?.flags ?? []).flatMap((f) => [
            {
              value: flagValue(f.name),
              label: `${f.name} · ${t('sessions.filter.anyVariant')}`,
              sessions: f.sessions,
            },
            ...f.variants.map((v) => ({
              value: flagValue(f.name, v.variant),
              label: `${f.name} = ${v.variant}`,
              sessions: v.sessions,
            })),
          ])
  const more =
    kind === 'device'
      ? !!facets.data?.devices.more
      : kind === 'os'
        ? !!facets.data?.os_versions.more
        : all.length > 20
  const shown = all.filter((v) => matches(v, text))
  // Device and OS take any value: the typed text as an exact one, last.
  const free = kind !== 'flag' && text.trim() !== '' && !shown.some((v) => v.value === text.trim())
  const count = shown.length + (free ? 1 : 0)
  const pending = kind === 'flag' ? flags.isPending : facets.isPending
  // Nothing to pick (a project without flags, or text that matches nothing): no listbox, which
  // must hold options, but the same focusable element with the sentence as a status, so the keys
  // (Esc) still work.
  const empty = !pending && count === 0
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new filter starts at the top
  useEffect(() => setActive(0), [text])
  useEffect(() => {
    if (more) field.current?.focus()
    else list.current?.focus()
  }, [more])
  useEffect(() => {
    list.current
      ?.querySelector(`#${CSS.escape(`${id}-o${active}`)}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [active, id])

  const choose = (i: number) => {
    if (i < shown.length) onPick((shown[i] as FilterValue).value)
    else if (free) onPick(text.trim())
  }
  const onKey = (e: KeyboardEvent) => {
    const go = (i: number) => {
      e.preventDefault()
      if (count) setActive((i + count) % count)
    }
    if (e.key === 'ArrowDown') go(active + 1)
    else if (e.key === 'ArrowUp') go(active - 1)
    else if (e.key === 'Home' && !more) go(0)
    else if (e.key === 'End' && !more) go(count - 1)
    else if (e.key === 'Enter') {
      e.preventDefault()
      choose(active)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      if (back) onBack()
      else onClose()
    } else if (e.key === 'Tab') onClose(false)
  }
  const option = (i: number) => `${id}-o${i}`
  const countText = (n: number) =>
    t('sessions.filter.sessions', { count: n, n: compactCount(i18n, n) })

  return (
    <div>
      <div className="us-ph">
        {back ? (
          <button type="button" className="us-back" onClick={onBack}>
            <Icon name="left" small />
            {t(KIND_NAME[kind])}
          </button>
        ) : (
          <span className="us-pk">{t(KIND_NAME[kind])}</span>
        )}
      </div>
      {more ? (
        <input
          ref={field}
          className="input us-pf"
          role="combobox"
          aria-expanded={empty ? 'false' : 'true'}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={count ? option(active) : undefined}
          aria-label={t('sessions.filter.filter')}
          placeholder={t('sessions.filter.filter')}
          spellCheck={false}
          autoComplete="off"
          translate="no"
          maxLength={kind === 'os' ? 64 : 128}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
        />
      ) : null}
      {empty ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: nothing to list, but it keeps the focus and Esc
        <div
          ref={list}
          id={`${id}-list`}
          className="us-pl"
          tabIndex={more ? -1 : 0}
          onKeyDown={more ? undefined : onKey}
        >
          <p className="us-pe" role="status">
            {t(all.length === 0 ? KIND_NOTHING[kind] : 'sessions.filter.none')}
          </p>
        </div>
      ) : (
        <div
          ref={list}
          id={`${id}-list`}
          role="listbox"
          className="us-pl"
          aria-label={t(KIND_NAME[kind])}
          tabIndex={more ? -1 : 0}
          aria-activedescendant={!more && count ? option(active) : undefined}
          onKeyDown={more ? undefined : onKey}
          aria-busy={pending || undefined}
        >
          {pending ? (
            <p className="us-pe" role="presentation">
              {t('state.loading')}
            </p>
          ) : null}
          {shown.map((v, i) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox or the combobox takes the keys
            <div
              key={v.value}
              id={option(i)}
              role="option"
              // The listbox or the combobox keeps the focus (aria-activedescendant).
              tabIndex={-1}
              aria-selected={i === active}
              className={i === active ? 'us-po on' : 'us-po'}
              onClick={() => choose(i)}
              onPointerMove={() => setActive(i)}
            >
              <span translate="no">{v.label}</span>
              <span className="us-pn">{countText(v.sessions)}</span>
            </div>
          ))}
          {free ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox takes the keys
            <div
              id={option(shown.length)}
              role="option"
              // The listbox or the combobox keeps the focus (aria-activedescendant).
              tabIndex={-1}
              aria-selected={active === shown.length}
              className={active === shown.length ? 'us-po us-use on' : 'us-po us-use'}
              onClick={() => choose(shown.length)}
              onPointerMove={() => setActive(shown.length)}
            >
              <span>{t('sessions.filter.use', { value: text.trim() })}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
